import { useState, type JSX, type KeyboardEvent } from 'react';
import { Dialog } from '../../components/Dialog';
import type { SettingsTab } from '../../uiStore';
import { AboutTab, AudioTab, GeneralTab, VideoTab } from './BasicTabs';
import { ChannelTab } from './ChannelTab';
import { HotkeysTab } from './HotkeysTab';
import { OutputTab } from './OutputTab';
import { PluginsTab } from './PluginsTab';
import { StreamTab } from './StreamTab';
import '../dialogs.css';

const TABS: readonly { id: SettingsTab; label: string; render(): JSX.Element }[] = [
  { id: 'general', label: 'General', render: () => <GeneralTab /> },
  { id: 'video', label: 'Video', render: () => <VideoTab /> },
  { id: 'audio', label: 'Audio', render: () => <AudioTab /> },
  { id: 'stream', label: 'Stream', render: () => <StreamTab /> },
  { id: 'channel', label: 'Channel', render: () => <ChannelTab /> },
  { id: 'output', label: 'Output / Recording', render: () => <OutputTab /> },
  { id: 'hotkeys', label: 'Hotkeys', render: () => <HotkeysTab /> },
  { id: 'plugins', label: 'Plugins', render: () => <PluginsTab /> },
  { id: 'about', label: 'About / Limitations', render: () => <AboutTab /> },
];

export function SettingsDialog({ initialTab, onClose }: { initialTab?: SettingsTab; onClose(): void }): JSX.Element {
  const [tab, setTab] = useState<SettingsTab>(initialTab ?? 'general');
  const active = TABS.find((t) => t.id === tab) ?? TABS[0]!;

  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>): void => {
    if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') return;
    event.preventDefault();
    const index = TABS.indexOf(active);
    const next = TABS[(index + (event.key === 'ArrowDown' ? 1 : -1) + TABS.length) % TABS.length]!;
    setTab(next.id);
    event.currentTarget.querySelector<HTMLElement>(`#settings-tab-${next.id}`)?.focus();
  };

  return (
    <Dialog
      title="Settings"
      onClose={onClose}
      width={900}
      height={640}
      flush
      footer={
        <button type="button" className="ui-btn primary" onClick={onClose}>
          Close
        </button>
      }
    >
      <div className="ui-tabs" role="tablist" aria-label="Settings sections" aria-orientation="vertical" onKeyDown={onKeyDown}>
        {TABS.map((t) => (
          <button
            key={t.id}
            id={`settings-tab-${t.id}`}
            type="button"
            role="tab"
            className="ui-tab"
            aria-selected={t.id === active.id}
            aria-controls="settings-page"
            tabIndex={t.id === active.id ? 0 : -1}
            onClick={() => setTab(t.id)}
          >
            {t.label}
          </button>
        ))}
      </div>
      <div id="settings-page" className="settings-page" role="tabpanel" aria-labelledby={`settings-tab-${active.id}`}>
        {active.render()}
      </div>
    </Dialog>
  );
}
